/**
 * Modal adapter.
 *
 * Why: the library's Modal does not keep keyboard focus inside itself. With a
 * dialog open, Tab walked on into the page behind it (issue #10503), and
 * closing it left focus nowhere in particular. Every dialog in the app comes
 * through @components/ui, so the fix lives here once instead of in each one.
 *
 * While it is open:
 *  - focus is put inside the dialog if the library did not already;
 *  - Tab on the last control goes to the first, Shift+Tab on the first goes to
 *    the last (nothing behind the dialog is reached by the keyboard);
 *  - on close, focus goes back to the element that had it when the dialog
 *    opened (the button that opened it).
 *
 * Deliberately modest: the keys are handled on the document, in the bubble
 * phase and only if nothing else already handled the Tab, so a future library
 * trap is not doubled up. Focus sitting OUTSIDE the dialog on something other
 * than the page body (a menu a dropdown renders into a portal) is left alone.
 * The dialog is found from a hidden marker rendered inside it, so no extra
 * props, refs or wrappers are needed by the 16 places that use it.
 */
import { Modal as DsModal } from '@amd-deae-ceme/design-system';
import { useEffect, useRef } from 'react';

const FOCUSABLE = [
  'a[href]',
  'button',
  'input',
  'select',
  'textarea',
  'summary',
  '[tabindex]',
  '[contenteditable="true"]',
].join(',');

/** What Tab can reach inside `root`, in DOM order. */
function tabbablesIn(root) {
  return Array.from(root.querySelectorAll(FOCUSABLE)).filter(
    (el) =>
      !el.disabled &&
      el.tabIndex >= 0 &&
      el.getAttribute('aria-hidden') !== 'true' &&
      !el.closest('[hidden], [inert]'),
  );
}

const isPageBody = (el) => !el || el === document.body || el === document.documentElement;

export function Modal({ open, children, ...props }) {
  const marker = useRef(null);
  // The element that had focus when the dialog opened. Taken while rendering,
  // before the library moves focus into the dialog.
  const opener = useRef(null);
  if (!open) opener.current = null;
  else if (opener.current === null && typeof document !== 'undefined') {
    opener.current = document.activeElement;
  }

  useEffect(() => {
    if (!open) return undefined;
    const dialog = () => marker.current?.closest('[role="dialog"]') ?? null;

    // Focus goes into the dialog if the library has not put it there already.
    const root = dialog();
    if (root && !root.contains(document.activeElement)) {
      (tabbablesIn(root)[0] ?? root).focus?.();
    }

    const onKeyDown = (e) => {
      if (e.key !== 'Tab' || e.defaultPrevented) return;
      const box = dialog();
      if (!box) return;
      const items = tabbablesIn(box);
      if (items.length === 0) {
        e.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;

      if (!box.contains(active)) {
        // Focus is on the page body (e.g. after a click on the backdrop): bring
        // it in. Anywhere else outside (a portalled menu) is not ours to move.
        if (isPageBody(active)) {
          e.preventDefault();
          (e.shiftKey ? last : first).focus();
        }
        return;
      }
      if (e.shiftKey && (active === first || active === box)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      // Back to the opener — if it is still on the page (a route change takes
      // it away with everything else).
      const back = opener.current;
      if (back && back.isConnected && typeof back.focus === 'function') back.focus();
    };
  }, [open]);

  return (
    <DsModal open={open} {...props}>
      {children}
      <span ref={marker} hidden />
    </DsModal>
  );
}

export default Modal;
